import {
  TableCellProps,
  WbButton,
  WbMoreOptions,
  WbTable,
  WbResponsiveTableToolbar,
  createActionsColumn,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import { RoleEntity } from '@agilelab/plugin-wb-rbac-common';
import { ErrorPanel } from '@mainstage/core-components';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { ListItemIcon, makeStyles, MenuItem, Theme } from '@material-ui/core';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { roleDetailRouteRef } from '../../../routes';
import { NewEditRoleDrawer } from '../RoleDrawer';
import { RoleFormType } from '../RoleForm';
import { useRolesTableContext } from './RolesTableContext';

interface RolesTableProps {}

const useStyles = makeStyles(
  (theme: Theme) => ({
    tableContainer: {
      height: '100%',
      flex: '1 1 0',
      [theme.breakpoints.down('sm')]: {
        height: 'auto',
        flex: '1 1 auto',
      },
    },
  }),
  { name: 'RolesTable' },
);

const RolesActions: FC = () => {
  const {
    selectedDeleteItems,
    openDrawer,
    setOpenDrawer,
    error,
    canDeleteNewRolePermission,
    canAddNewRolePermission,
    setOpenConfirmDialog,
    addRole,
    addRoleState,
    cloneDefaults,
    setCloneDefaults,
    cloneRole,
    cloneRoleState,
  } = useRolesTableContext();

  const onSubmit = useCallback(
    (newValues: RoleFormType) => {
      addRole(newValues);
    },
    [addRole],
  );

  const onCloneSubmit = useCallback(
    (newValues: RoleFormType) => {
      cloneRole(newValues);
    },
    [cloneRole],
  );

  const cloneDrawerOpen = cloneDefaults !== undefined;
  const setCloneDrawerOpen = useCallback(
    (open: boolean | ((prev: boolean) => boolean)) => {
      const resolved =
        typeof open === 'function' ? open(cloneDrawerOpen) : open;
      if (!resolved) setCloneDefaults(undefined);
    },
    [cloneDrawerOpen, setCloneDefaults],
  );

  return (
    <>
      {!!selectedDeleteItems?.length
        ? canDeleteNewRolePermission && (
            <>
              <WbButton
                label="Delete selected"
                startIcon={<DeleteIcon />}
                onClick={() => setOpenConfirmDialog(true)}
                color="secondary"
                size="small"
                variant="outlined"
              />
            </>
          )
        : canAddNewRolePermission && (
            <WbButton
              label="Add Role"
              startIcon={<AddCircleIcon />}
              onClick={() => setOpenDrawer(true)}
              color="primary"
              size="small"
            />
          )}
      <NewEditRoleDrawer
        onSubmit={onSubmit}
        dataState={addRoleState}
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        error={error}
      />
      <NewEditRoleDrawer
        onSubmit={onCloneSubmit}
        dataState={cloneRoleState}
        openDrawer={cloneDrawerOpen}
        setOpenDrawer={setCloneDrawerOpen}
        defaultValues={cloneDefaults}
        title="Clone Role"
        submitLabel="Clone"
        showCloneAssignments
        error={error}
      />
    </>
  );
};

export const RolesCardHeader: FC = () => {
  const {
    selectedDeleteItems,
    setSelectedDeleteItems,
    filters,
    setFilters,
    setPagination,
  } = useRolesTableContext();

  return (
    <WbResponsiveTableToolbar
      searchProps={{
        value: filters.text,
        onChange: search => {
          setFilters({ text: search });
          setPagination(p => ({ ...p, offset: 0 }));
        },
      }}
      selectedProps={{
        selectedLabel: 'Role',
        selectedCount: selectedDeleteItems?.length ?? 0,
        onClearSelection: () => setSelectedDeleteItems([]),
      }}
      onClear={() => setFilters({})}
    >
      <WbResponsiveTableToolbar.Actions>
        <RolesActions />
      </WbResponsiveTableToolbar.Actions>
    </WbResponsiveTableToolbar>
  );
};

const Actions = ({ row }: { row: RoleEntity }) => {
  const {
    setSelectedRow,
    selectedRow,
    setOpenConfirmDialog,
    canDeleteNewRolePermission,
    canAddNewRolePermission,
    setCloneDefaults,
    checkIfDisabled,
  } = useRolesTableContext();

  const navigate = useNavigate();
  const roleDetailPageRef = useRouteRef(roleDetailRouteRef);

  const onMenuClose = useCallback(() => {
    setSelectedRow(undefined);
  }, [setSelectedRow]);

  const items = useMemo(() => {
    const defaultItems = [
      <MenuItem
        key={`${row.id}-manage`}
        onClick={() => {
          navigate({
            pathname: `${roleDetailPageRef({ roleId: row.id })}/assignments`,
          });
        }}
      >
        <ListItemIcon>
          <AccountBoxIcon fontSize="small" />
        </ListItemIcon>
        Add Assignments
      </MenuItem>,
    ];

    if (canAddNewRolePermission) {
      defaultItems.push(
        <MenuItem
          key={`${row.id}-clone`}
          onClick={() => {
            setCloneDefaults({
              displayName: row.displayName ?? '',
              description: row.description,
              sourceRoleId: row.id,
            });
            onMenuClose();
          }}
        >
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          Clone Role
        </MenuItem>,
      );
    }

    if (canDeleteNewRolePermission && !checkIfDisabled(row)) {
      defaultItems.push(
        <MenuItem
          key={`${row.id}-delete`}
          onClick={() => {
            setOpenConfirmDialog(true);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete Role
        </MenuItem>,
      );
    }
    return defaultItems;
  }, [
    canAddNewRolePermission,
    canDeleteNewRolePermission,
    checkIfDisabled,
    navigate,
    onMenuClose,
    roleDetailPageRef,
    row,
    setCloneDefaults,
    setOpenConfirmDialog,
  ]);

  return !!items?.length ? (
    <WbMoreOptions
      key={row.id}
      open={selectedRow?.id === row.id}
      MenuProps={{ onClick: e => e.stopPropagation() }}
      onClick={e => {
        e.stopPropagation();
        setSelectedRow(row);
      }}
      onClose={onMenuClose}
    >
      {items}
    </WbMoreOptions>
  ) : (
    <></>
  );
};

export const RolesTable: FC<RolesTableProps> = () => {
  const {
    selectedDeleteItems,
    setSelectedDeleteItems,
    rolesLoaded,
    pagination,
    setPagination,
    canDeleteNewRolePermission,
    checkIfDisabled,
  } = useRolesTableContext();

  const classes = useStyles();
  const navigate = useNavigate();
  const roleDetailPageRef = useRouteRef(roleDetailRouteRef);

  const columns: TableCellProps<RoleEntity>[] = [
    {
      field: 'id',
      headerName: 'ID',
      fieldRender: role => (
        <WbLink
          to={roleDetailPageRef({ roleId: role.id })}
          truncate
          showTooltip
        >
          {role.id}
        </WbLink>
      ),
    },
    {
      field: 'displayName',
      headerName: 'Name',
    },
    {
      field: 'description',
      tooltip: true,
      headerName: 'Description',
    },
    createActionsColumn({ renderActions: row => <Actions row={row} /> }),
  ];

  if (rolesLoaded.error)
    return (
      <ErrorPanel
        error={
          rolesLoaded.error instanceof Error
            ? rolesLoaded.error
            : new Error('Something went wrong')
        }
      />
    );

  const onClickHandle = (
    row: RoleEntity,
    event: MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => {
    event.preventDefault();

    event.stopPropagation();
    navigate({ pathname: roleDetailPageRef({ roleId: row.id }) });
  };

  const getRowId = (r: RoleEntity) => r.id;

  return (
    <WbTable<RoleEntity>
      selection={canDeleteNewRolePermission ? 'multiple' : undefined}
      selectedItems={selectedDeleteItems}
      setSelectedItems={setSelectedDeleteItems}
      checkIfDisabled={checkIfDisabled}
      tooltipIfDisabled="This is a platform permission. You cannot delete"
      classNames={{
        container: classes.tableContainer,
      }}
      components={{
        tableLoader: {
          loading: rolesLoaded.loading,
        },
        tableContent: {
          columns,
          getRowId,
          rows: rolesLoaded.value?.roles,
          onRowClick: onClickHandle,
        },
      }}
      pagination={{
        rowsPerPageOptions: [25, 50, 100],
        count: rolesLoaded.value?.total || 0,
        limit: pagination.limit,
        offset: pagination.offset,
        onPageChange: (page: number) => {
          setPagination({
            ...pagination,
            offset: page * pagination.limit,
          });
        },
        onRowsPerPageChange: (rowsPerPage: number) => {
          setPagination({
            offset: 0,
            limit: rowsPerPage,
          });
        },
      }}
    />
  );
};
